import { supabase } from "@/integrations/supabase/client";

export type CreatorForProduct = Pick<Creator, "id" | "name" | "handle" | "avatar_url">;

export type Product = {
  id: string;
  title: string;
  brand: string;
  price: number;
  price_cents?: number | null;
  currency: string;
  image_key: string;
  network: "Impact" | "Levanta" | "ShareASale" | "Amazon";
  in_stock: boolean;
  category:
    | "Womens Fashion"
    | "Beauty & Skincare"
    | "Mother & Baby"
    | "Home & Decor"
    | "Fitness & Wellness"
    | "Shoes & Accessories"
    | "Cooking & Grocery"
    | "Finance & Lifestyle"
    | "Travel"
    | "GRWM";
  url: string;
  commission: number;
  description?: string | null;
  rating?: number | null;
  ratings_total?: number | null;
  creator_handle?: string | null;
  creator_name?: string | null;
};

export type Creator = {
  id: string;
  user_id: string | null;
  handle: string;
  name: string;
  bio: string | null;
  avatar_url: string | null;
  cover_url: string | null;
  followers: string | null;
  status: "pending" | "approved" | "live" | "rejected";
  instagram?: string | null;
  tiktok?: string | null;
  youtube?: string | null;
  website?: string | null;
  created_at?: string;
  onboarding_dismissed?: boolean;
  is_verified?: boolean;
};

export type Collection = {
  id: string;
  creator_id: string;
  title: string;
  cover_url: string | null;
};

export async function fetchProducts(): Promise<Product[]> {
    const { data, error } = await supabase
      .from("products")
      .select("*, creator_products(creators:creator_id(handle, name))")
      .order("created_at", { ascending: true })
      .limit(48);
    if (error || !data) return [];
    return (data as any[]).map((row) => {
      const cp = Array.isArray(row.creator_products) ? row.creator_products[0] : null;
      const c = cp?.creators ?? null;
      const { creator_products, ...rest } = row;
      return {
        ...rest,
        creator_handle: c?.handle ?? null,
        creator_name: c?.name ?? null,
      } as Product;
    });
}

export async function fetchNewProducts(): Promise<Product[]> {
    const { data, error } = await supabase
      .from("products")
      .select("*, creator_products(creators:creator_id(handle, name))")
      .order("created_at", { ascending: false })
      .limit(6);
    if (error || !data) return [];
    return (data as any[]).map((row) => {
      const cp = Array.isArray(row.creator_products) ? row.creator_products[0] : null;
      const c = cp?.creators ?? null;
      const { creator_products, ...rest } = row;
      return {
        ...rest,
        creator_handle: c?.handle ?? null,
        creator_name: c?.name ?? null,
      } as Product;
    });
}

export type CatalogFilters = {
  q?: string;
  category?: Product["category"] | "All";
  brand?: string;
  inStock?: boolean;
  minPrice?: number;
  maxPrice?: number;
  page?: number;
  pageSize?: number;
};

export async function searchCatalog(filters: CatalogFilters) {
  const pageSize = filters.pageSize ?? 24;
  const page = filters.page ?? 0;
  let q = supabase
    .from("products")
    .select("*", { count: "exact" })
    .order("commission", { ascending: false })
    .range(page * pageSize, page * pageSize + pageSize - 1);

  if (filters.q && filters.q.trim()) {
    const term = filters.q.trim().replace(/[%_]/g, " ");
    q = q.or(`title.ilike.%${term}%,brand.ilike.%${term}%`);
  }
  if (filters.category && filters.category !== "All") q = q.eq("category", filters.category);
  if (filters.brand) q = q.eq("brand", filters.brand);
  if (filters.inStock !== undefined) q = q.eq("in_stock", filters.inStock);
  if (filters.minPrice !== undefined) q = q.gte("price", filters.minPrice);
  if (filters.maxPrice !== undefined) q = q.lte("price", filters.maxPrice);

  const { data, error, count } = await q;
  if (error) throw error;
  return { items: (data ?? []) as Product[], total: count ?? 0, page, pageSize };
}

export async function reorderCreatorProducts(creatorId: string, orderedProductIds: string[]) {
  await Promise.all(
    orderedProductIds.map((id, idx) =>
      supabase
        .from("creator_products")
        .update({ position: idx })
        .eq("creator_id", creatorId)
        .eq("product_id", id),
    ),
  );
}

export async function fetchCollection(collectionId: string) {
  const { data, error } = await supabase
    .from("collections")
    .select("*, collection_products(position, product_id, products(*))")
    .eq("id", collectionId)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  const items = (data.collection_products ?? [])
    .sort((a: any, b: any) => a.position - b.position)
    .map((r: any) => ({ position: r.position as number, product: r.products as Product }))
    .filter((r: any) => r.product);
  return { id: data.id as string, title: data.title as string, cover_url: data.cover_url as string | null, creator_id: data.creator_id as string, items };
}

export async function setCollectionProducts(collectionId: string, orderedProductIds: string[]) {
  await supabase.from("collection_products").delete().eq("collection_id", collectionId);
  if (orderedProductIds.length === 0) return;
  const rows = orderedProductIds.map((product_id, position) => ({ collection_id: collectionId, product_id, position }));
  const { error } = await supabase.from("collection_products").insert(rows);
  if (error) throw error;
}

export async function updateCollection(collectionId: string, patch: { title?: string; cover_url?: string | null }) {
  const { error } = await supabase.from("collections").update(patch).eq("id", collectionId);
  if (error) throw error;
}

export async function fetchProduct(id: string): Promise<Product | null> {
    const { data, error } = await supabase
      .from("products")
      .select("*")
      .eq("id", id)
      .maybeSingle();
    if (error || !data) return null;
    return data as Product;
}

export async function fetchCreators(opts?: { status?: Creator["status"][] }): Promise<Creator[]> {
    let q = (supabase as any).from("public_creators").select("*").order("created_at", { ascending: true });
    if (opts?.status) q = q.in("status", opts.status);
    const { data, error } = await q;
    if (error || !data) return [];
    return data as Creator[];
}

export async function fetchCreatorByHandle(handle: string) {
  let creator: Creator | null = null;
    const { data } = await (supabase as any)
      .from("public_creators")
      .select("*")
      .eq("handle", handle)
      .maybeSingle();
    creator = (data as Creator | null) ?? null;
  if (!creator) return null;

  const [{ data: cp }, { data: collections }] = await Promise.all([
    supabase
      .from("creator_products")
      .select("product_id, position, products(*)")
      .eq("creator_id", creator.id)
      .order("position"),
    supabase
      .from("collections")
      .select("*, collection_products(product_id)")
      .eq("creator_id", creator.id)
      .order("created_at"),
  ]);

  const products = (cp ?? [])
    .map((row) => row.products as unknown as Product)
    .filter(Boolean);

  return {
    creator: creator as Creator,
    products,
    collections: (collections ?? []) as (Collection & {
      collection_products: { product_id: string }[];
    })[],
  };
}

export async function fetchCreatorForUser(userId: string): Promise<Creator | null> {
  const { data, error } = await supabase
    .from("creators")
    .select("*")
    .eq("user_id", userId)
    .maybeSingle();
  if (error) throw error;
  return data as Creator | null;
}

export async function fetchCreatorProducts(creatorId: string): Promise<Product[]> {
  const { data, error } = await supabase
    .from("creator_products")
    .select("position, products(*)")
    .eq("creator_id", creatorId)
    .order("position");
  if (error) throw error;
  return (data ?? []).map((r) => r.products as unknown as Product).filter(Boolean);
}

export async function fetchCreatorCollections(creatorId: string) {
  const { data, error } = await supabase
    .from("collections")
    .select("*, collection_products(product_id)")
    .eq("creator_id", creatorId)
    .order("created_at");
  if (error) throw error;
  return (data ?? []) as (Collection & { collection_products: { product_id: string }[] })[];
}

export type FeaturedLook = {
  id: string;
  title: string | null;
  image_path: string;
  creator_handle: string;
};

export async function fetchFeaturedLooks(): Promise<FeaturedLook[]> {
  try {
    const { data, error } = await supabase
      .from("looks")
      .select("id, title, image_path, creators(handle)")
      .eq("featured", true)
      .order("created_at", { ascending: false })
      .limit(3);
    if (error || !data) return [];
    return (data as any[]).map((row) => ({
      id: row.id,
      title: row.title,
      image_path: row.image_path,
      creator_handle: row.creators?.handle ?? "",
    }));
  } catch (err) {
    console.error("fetchFeaturedLooks failed:", err);
    return [];
  }
}

export async function fetchCreatorsForProduct(productId: string): Promise<CreatorForProduct[]> {
  const { data, error } = await supabase
    .from("creator_products")
    .select("creators(id, name, handle, avatar_url)")
    .eq("product_id", productId)
    .eq("creators.status", "live")
    .limit(8);
  if (error || !data) return [];
  return (data as any[])
    .map((row) => row.creators)
    .filter(Boolean) as CreatorForProduct[];
}

export async function fetchCreatorBio(handle: string) {
  const { data: creator } = await (supabase as any)
    .from("public_creators")
    .select("*")
    .eq("handle", handle)
    .maybeSingle();
  if (!creator) return null;

  const { data: cp } = await supabase
    .from("creator_products")
    .select("created_at, products(*)")
    .eq("creator_id", (creator as Creator).id)
    .order("created_at", { ascending: false })
    .limit(8);

  const products = (cp ?? [])
    .map((row: any) => row.products as Product)
    .filter(Boolean);

  return { creator: creator as Creator, products };
}

/** Trending products from the hourly-refreshed materialized view. */
export async function fetchTrending(): Promise<Product[]> {
  try {
    const { data, error } = await supabase
      .from("trending_products")
      .select("*")
      .limit(12);
    if (error || !data) return [];
    return (data as any[]).map((row) => ({
      ...row,
      url: row.affiliate_url ?? row.url ?? "",
    })) as Product[];
  } catch (err) {
    console.error("fetchTrending failed:", err);
    return [];
  }
}
